import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Flex,
  Text,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  useColorModeValue,
  Button,
  Input,
  InputGroup,
  InputLeftElement,
  HStack,
  Image,
  Spinner,
  Alert,
  AlertIcon,
  AlertTitle,
  AlertDescription,
  Badge,
  FormControl,
  FormLabel,
  NumberInput,
  NumberInputField,
  VStack,
  Select,
  Switch,
  Heading,
  Icon,
  IconButton,
  Tooltip,
  Code,
} from "@chakra-ui/react";
import { SearchIcon, CheckIcon, WarningIcon } from "@chakra-ui/icons";
import { FaPlus, FaEdit, FaTrash, FaBoxOpen, FaSync, FaInfoCircle, FaBox, FaExternalLinkAlt } from "react-icons/fa";
import { useQuery, useMutation, useQueryClient } from 'react-query';
import toast from 'react-hot-toast';

// سرویس‌های واقعی بک‌اند
import { getProducts, deleteProduct, updateProductStock, updateProduct, syncProductStock } from '../services/productService';
import { getGateways } from '../services/gatewayService';
import CustomModal from '../components/Modal'; // مسیر ایمپورت خود را چک کنید
import ProductVariantTree from '../components/ProductVariantTree';
import { usePersistedPagination } from "../hooks/usePersistedPagination";
import api from '../utils/api';
import { useAuth } from '../contexts/AuthContext';

function getProductPublicPageUrl(productId) {
  const base = (api.defaults.baseURL || '').replace(/\/api\/?$/, '');
  return `${base}/p/${productId}`;
}

function getShopPublicPageUrl(userId) {
  const base = (api.defaults.baseURL || '').replace(/\/api\/?$/, '');
  return `${base}/shop/${userId}`;
}
export default function Products() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { user } = useAuth();
  // 🎨 متغیرهای رنگی برای تم روشن/تاریک (SaaS Design)
  const bgPage = useColorModeValue("gray.50", "gray.900");
  const cardBg = useColorModeValue("white", "gray.800");
  const textColor = useColorModeValue("gray.800", "white");
  const mutedColor = useColorModeValue("gray.500", "gray.400");
  const borderColor = useColorModeValue("gray.100", "whiteAlpha.200");
  const hoverBg = useColorModeValue("gray.50", "whiteAlpha.50");
  const theadBg = useColorModeValue("gray.50", "gray.700");
  const filterInputBg = useColorModeValue("gray.50", "gray.900");
  const filterSelectBg = useColorModeValue("gray.50", "gray.900");
  const productImagePlaceholderBg = useColorModeValue("gray.100", "gray.700");
  const skuCodeBg = useColorModeValue("slate.50", "whiteAlpha.100");
  const actionsBarBg = useColorModeValue("gray.100", "whiteAlpha.100");
  const paginationBg = useColorModeValue("white", "gray.800");
  const stockModalHeaderBg = useColorModeValue("blue.50", "blue.900");
  const stockModalHeaderSubTextColor = useColorModeValue("blue.600", "blue.300");
  const stockLevelHighlightBg = useColorModeValue("blue.50", "blue.900");
  const stockVariantSelectBg = useColorModeValue("gray.50", "gray.900");
  const stockNumberInputBg = useColorModeValue("gray.50", "gray.900");
  const stockGatewayRowBg = useColorModeValue("gray.50", "whiteAlpha.50");

  // State فیلترها و صفحه‌بندی
  const [searchTerm, setSearchTerm] = useState('');
  const [filterGateway, setFilterGateway] = useState('');
  const [sortBy, setSortBy] = useState('totalStock');
  
  const {
    page: currentPage,
    setPage: setCurrentPage,
    limit: itemsPerPage,
    // setLimit: setItemsPerPage,
  } = usePersistedPagination("productsTable", { defaultPage: 1, defaultLimit: 10 });

  // State مودال‌ها
  const [stockModalProduct, setStockModalProduct] = useState(null);
  const [stockApplyTo, setStockApplyTo] = useState('product');
  const [stockLogicalVariantId, setStockLogicalVariantId] = useState('');
  const [stockValue, setStockValue] = useState(0);
  const [stockPriceValue, setStockPriceValue] = useState(0);
  const [stockGatewayStatuses, setStockGatewayStatuses] = useState({});
  const [detailsModalProduct, setDetailsModalProduct] = useState(null);
  
  // ==========================================
  // دریافت لیست درگاه‌ها و محصولات
  // ==========================================
  const { data: gatewaysData } = useQuery(['gateways'], getGateways);
  const gateways = gatewaysData?.data || [];

  const {
    data: productsData,
    isLoading,
    error,
    refetch
  } = useQuery(
    ['products', currentPage, itemsPerPage, searchTerm, filterGateway, sortBy],
    () => getProducts({
      page: currentPage,
      limit: itemsPerPage,
      search: searchTerm,
      ...(filterGateway && { gateway: filterGateway }),
      sortBy: sortBy || 'totalStock',
    }),
    {
      keepPreviousData: true,
      staleTime: 2 * 60 * 1000,
    }
  );

  // ==========================================
  // Mutations (تغییرات داده)
  // ==========================================
  const deleteProductMutation = useMutation(deleteProduct, {
    onSuccess: () => {
      toast.success('محصول با موفقیت حذف شد');
      queryClient.invalidateQueries(['products']);
    },
    onError: (error) => {
      toast.error(error.response?.data?.message || 'خطا در حذف محصول');
    }
  });

  const updateStockMutation = useMutation(
    ({ productId, data }) => updateProductStock(productId, data),
    {
      onSuccess: () => {
        toast.success('موجودی در پلتفرم‌ها و دیتابیس به‌روز شد');
        queryClient.invalidateQueries(['products']);
        handleCloseStockModal();
      },
      onError: (error) => {
        const msg = error.response?.data?.message || 'خطا در به‌روزرسانی موجودی';
        const errors = error.response?.data?.errors || [];

        if (stockModalProduct?.gatewayRefs?.length) {
          const nextStatuses = { ...stockGatewayStatuses };
          stockModalProduct.gatewayRefs.forEach((ref) => {
            const gwName = ref.gateway?.name || (typeof ref.gateway === 'string' ? ref.gateway : '');
            if (!gwName) return;
            const hasErrorForGw = errors.some((e) => typeof e === 'string' && e.includes(`(${gwName})`));
            nextStatuses[gwName] = hasErrorForGw ? 'error' : 'success';
          });
          setStockGatewayStatuses(nextStatuses);
        }
        toast.error(errors.length ? `${msg}: ${errors.join('؛ ')}` : msg);
      },
    }
  );

  const syncStockMutation = useMutation(
    ({ productId, data }) => syncProductStock(productId, data),
    {
      onSuccess: (response) => {
        const data = response.data || {};
        const { syncResults = [] } = data;
        let message = response.message || 'موجودی با موفقیت همگام شد';
        
        if (syncResults.length > 1) {
          const successfulVariants = syncResults.filter(r => r.success).map(r => r.name);
          const failedVariants = syncResults.filter(r => !r.success).map(r => r.name);
          if (successfulVariants.length > 0) message += `\n✅ موفق: ${successfulVariants.join('، ')}`;
          if (failedVariants.length > 0) message += `\n❌ ناموفق: ${failedVariants.join('، ')}`;
        }
        toast.success(message);
        queryClient.invalidateQueries(['products']);
      },
      onError: (error) => {
        const msg = error.response?.data?.message || 'خطا در همگام‌سازی موجودی';
        const data = error.response?.data?.data || {};
        const { syncResults = [], errors = [] } = data;
        let errorMessage = msg;
        
        if (syncResults.length > 1) {
          const successfulVariants = syncResults.filter(r => r.success).map(r => r.name);
          const failedVariants = syncResults.filter(r => !r.success).map(r => r.name);
          if (successfulVariants.length > 0) errorMessage += `\n✅ موفق: ${successfulVariants.join('، ')}`;
          if (failedVariants.length > 0) errorMessage += `\n❌ ناموفق: ${failedVariants.join('، ')}`;
        }
        if (errors.length > 0) errorMessage += `\n\nخطاها: ${errors.join('؛ ')}`;
        toast.error(errorMessage);
      },
    }
  );

  // ==========================================
  // Handlers
  // ==========================================
  const handleDeleteProduct = (productId) => {
    if (window.confirm('آیا از حذف این محصول اطمینان دارید؟')) {
      deleteProductMutation.mutate(productId);
    }
  };

  const openStockModal = (product) => {
    setStockModalProduct(product);
    setStockApplyTo('product');
    const firstLv = product?.logicalVariants?.[0];
    setStockLogicalVariantId(firstLv?._id || '');
    setStockValue(firstLv?.stock ?? 0);
    setStockPriceValue(firstLv?.price ?? 0);
    if (product?.gatewayRefs?.length) {
      const initialStatuses = {};
      product.gatewayRefs.forEach((ref) => {
        const gwName = ref.gateway?.name || (typeof ref.gateway === 'string' ? ref.gateway : '');
        if (gwName) initialStatuses[gwName] = 'idle';
      });
      setStockGatewayStatuses(initialStatuses);
    } else {
      setStockGatewayStatuses({});
    }
  };

  const handleCloseStockModal = () => {
    setStockModalProduct(null);
    setStockValue(0);
    setStockPriceValue(0);
    setStockApplyTo('product');
    setStockLogicalVariantId('');
    setStockGatewayStatuses({});
  };

  const handleVariantSelectChange = (e) => {
    const selectedId = e.target.value;
    setStockLogicalVariantId(selectedId);
    const lv = stockModalProduct?.logicalVariants?.find((v) => String(v._id) === String(selectedId));
    if (lv) {
      setStockValue(lv.stock ?? 0);
      setStockPriceValue(lv.price ?? 0);
    }
  };

  const handleSubmitStock = () => {
    if (!stockModalProduct) return;
    const applyTo = stockApplyTo;
    const logicalVariantId = applyTo === 'variant' ? stockLogicalVariantId : undefined;
    
    if (applyTo === 'variant' && !logicalVariantId) {
      toast.error('یک تنوع را انتخاب کنید');
      return;
    }
    
    if (stockModalProduct?.gatewayRefs?.length) {
      const loadingStatuses = {};
      stockModalProduct.gatewayRefs.forEach((ref) => {
        const gwName = ref.gateway?.name || (typeof ref.gateway === 'string' ? ref.gateway : '');
        if (gwName) loadingStatuses[gwName] = 'loading';
      });
      setStockGatewayStatuses(loadingStatuses);
    }

    updateStockMutation.mutate({
      productId: stockModalProduct._id,
      data: {
        stock: Math.max(0, Number(stockValue)),
        applyTo,
        ...(logicalVariantId && { logicalVariantId }),
        ...(applyTo === 'variant' && { price: Math.max(0, Number(stockPriceValue)) }),
      },
    });
  };

  const handleSyncStock = (product) => {
    if (!product.gatewayRefs || product.gatewayRefs.length === 0) {
      toast.error('این محصول به هیچ درگاهی متصل نیست');
      return;
    }
    const variantsCount = (product.logicalVariants || []).length;
    const gatewaysCount = product.gatewayRefs.length;
    let confirmMessage = variantsCount > 0 
      ? `آیا می‌خواهید موجودی و قیمت‌های ${variantsCount} تنوع این محصول را با ${gatewaysCount} درگاه همگام کنید؟\n\nموجودی تنوع‌ها به همراه بررسی و اصلاح خودکار قیمت بر اساس درصد مشخص‌شده برای هر درگاه ارسال خواهد شد.`
      : `آیا می‌خواهید موجودی و قیمت‌های این محصول را با ${gatewaysCount} درگاه همگام کنید؟`;

    if (window.confirm(confirmMessage)) {
      syncStockMutation.mutate({ productId: product._id, data: { applyTo: 'product' } });
    }
  };

  // ==========================================
  // وضعیت‌های بارگذاری و خطا
  // ==========================================
  if (isLoading) {
    return (
      <Flex direction="column" dir="rtl" minH="100vh" bg={bgPage} align="center" justify="center">
        <Box position="relative" mb={6}>
          <Spinner size="xl" thickness="4px" speed="0.65s" emptyColor="gray.200" color="blue.500" w="60px" h="60px" />
          <Icon as={FaBox} position="absolute" top="50%" left="50%" transform="translate(-50%, -50%)" color="blue.500" />
        </Box>
        <Text fontSize="lg" fontWeight="bold" color={textColor} animation="pulse">
          در حال بارگذاری لیست محصولات...
        </Text>
      </Flex>
    );
  }

  if (error) {
    return (
      <Flex direction="column" dir="rtl" minH="100vh" bg={bgPage} p={8}>
        <Alert status="error" borderRadius="2xl" boxShadow="sm" p={6}>
          <AlertIcon boxSize="24px" />
          <Box ml={4}>
            <AlertTitle fontSize="lg" mb={2}>خطا در بارگذاری داده‌ها!</AlertTitle>
            <AlertDescription color="gray.600">
              {error.message || 'مشکلی در ارتباط با سرور به وجود آمده است.'}
            </AlertDescription>
          </Box>
        </Alert>
        <Button mt={6} colorScheme="blue" alignSelf="start" onClick={() => refetch()} isLoading={isLoading}>
          تلاش مجدد
        </Button>
      </Flex>
    );
  }

  const products = productsData?.data || [];
  const pagination = productsData?.pagination || {};

  return (
    <>
      <Flex direction="column" dir="rtl" minH="100vh" bg={bgPage} pb={10}>
        <Box maxW="100%" mx="auto" w="full" px={{ base: 4, md: 8 }} py={8}>
          
          {/* هدر صفحه و فیلترها */}
          <Flex justify="space-between" align={{ base: "start", lg: "flex-end" }} mb={8} flexDir={{ base: "column", lg: "row" }} gap={6}>
            <Box>
              <Heading size="lg" color={textColor} letterSpacing="tight" mb={2}>
                مدیریت محصولات
              </Heading>
              <Text fontSize="sm" color={mutedColor}>
                لیست کالاها، تنوع‌ها و موجودی متمرکز شما
              </Text>
            </Box>

            <Flex gap={3} flexWrap="wrap" align="center">
              {user?._id && (
                <Button
                  leftIcon={<FaExternalLinkAlt />}
                  variant="outline"
                  colorScheme="purple"
                  borderRadius="xl"
                  onClick={() => window.open(getShopPublicPageUrl(user._id), '_blank', 'noopener,noreferrer')}
                >
                  صفحه فروشگاه
                </Button>
              )}
              <HStack spacing={3} bg={cardBg} p={2} borderRadius="2xl" borderWidth="1px" borderColor={borderColor} shadow="sm">
                <InputGroup size="md" w={{ base: "full", md: "250px" }}>
                  <InputLeftElement pointerEvents="none">
                    <SearchIcon color="gray.400" />
                  </InputLeftElement>
                  <Input
                    placeholder="جستجو کالا یا کد..."
                    borderRadius="xl"
                    bg={filterInputBg}
                    border="none"
                    _focus={{ ring: 2, ringColor: "blue.400" }}
                    value={searchTerm}
                    onChange={(e) => { setSearchTerm(e.target.value); setCurrentPage(1); }}
                  />
                </InputGroup>
                
                <Select
                  size="md"
                  borderRadius="xl"
                  w={{ base: "full", md: "160px" }}
                  bg={filterSelectBg}
                  border="none"
                  value={filterGateway}
                  onChange={(e) => { setFilterGateway(e.target.value); setCurrentPage(1); }}
                >
                  <option value="">همهٔ درگاه‌ها</option>
                  {gateways.map((g) => <option key={g._id} value={g._id}>{g.name}</option>)}
                </Select>

                <Select
                  size="md"
                  borderRadius="xl"
                  w={{ base: "full", md: "140px" }}
                  bg={filterSelectBg}
                  border="none"
                  value={sortBy}
                  onChange={(e) => { setSortBy(e.target.value); setCurrentPage(1); }}
                >
                  <option value="totalStock">موجودی کل</option>
                  <option value="createdAt">تاریخ ایجاد</option>
                </Select>
              </HStack>

              <Button
                leftIcon={<FaPlus />}
                bgGradient="linear(to-r, blue.500, cyan.400)"
                color="white"
                borderRadius="2xl"
                h="56px"
                px={6}
                fontSize="md"
                fontWeight="bold"
                boxShadow="0 10px 20px -5px rgba(59,130,246,0.4)"
                _hover={{ bgGradient: 'linear(to-r, blue.600, cyan.500)', transform: 'translateY(-2px)' }}
                onClick={() => navigate('/products/new')}
              >
                کالای جدید
              </Button>
            </Flex>
          </Flex>

          {/* بدنه اصلی (جدول) */}
          <Box bg={cardBg} borderRadius="2xl" borderWidth="1px" borderColor={borderColor} boxShadow="sm" overflow="hidden">
            <Box overflowX="auto" className="custom-scrollbar">
              <Table variant="simple" size="md">
                <Thead bg={theadBg}>
                  <Tr>
                    <Th color={mutedColor} fontSize="xs" textTransform="none" py={4} borderColor={borderColor}>مشخصات محصول</Th>
                    <Th color={mutedColor} fontSize="xs" textTransform="none" py={4} borderColor={borderColor}>کد (SKU)</Th>
                    <Th color={mutedColor} fontSize="xs" textTransform="none" py={4} borderColor={borderColor}>وضعیت موجودی</Th>
                    <Th color={mutedColor} fontSize="xs" textTransform="none" py={4} borderColor={borderColor}>پلتفرم‌های متصل</Th>
                    <Th color={mutedColor} fontSize="xs" textTransform="none" py={4} borderColor={borderColor}>فعال/غیرفعال</Th>
                    <Th color={mutedColor} fontSize="xs" textTransform="none" py={4} borderColor={borderColor} textAlign="center">عملیات سریع</Th>
                  </Tr>
                </Thead>
                <Tbody>
                  {products.length === 0 ? (
                    <Tr>
                      <Td colSpan={6} textAlign="center" py={16} borderBottom="none">
                        <Flex direction="column" align="center" justify="center">
                          <Icon as={FaBoxOpen} boxSize={12} color="gray.300" mb={4} />
                          <Text fontSize="lg" fontWeight="bold" color={textColor} mb={1}>محصولی یافت نشد</Text>
                          <Text fontSize="sm" color={mutedColor}>برای شروع، از دکمه افزودن کالای جدید استفاده کنید.</Text>
                        </Flex>
                      </Td>
                    </Tr>
                  ) : (
                    products.map((product) => {
                      const lv = product.logicalVariants || [];
                      const refs = product.gatewayRefs || [];
                      const totalLogicalStock = lv.reduce((s, v) => s + (v.stock || 0), 0);
                      const totalVariants = refs.reduce((acc, ref) => acc + (ref.variants?.length || 0), 0);
                      const totalVariantStock = refs.reduce((acc, ref) => acc + (ref.variants || []).reduce((sum, v) => sum + (v.stock || 0), 0), 0);
                      const stock = product.stock ?? 0;
                      
                      return (
                        <Tr key={product._id} _hover={{ bg: hoverBg }} transition="background 0.2s">
                          {/* 1. مشخصات محصول (عکس + نام) */}
                          <Td borderColor={borderColor} py={3}>
                            <HStack spacing={4}>
                              {product.image ? (
                                <Image src={product.image} alt={product.name} boxSize="50px" objectFit="cover" borderRadius="xl" shadow="sm" />
                              ) : (
                                <Flex boxSize="50px" bg={productImagePlaceholderBg} borderRadius="xl" align="center" justify="center">
                                  <Icon as={FaBox} color="gray.400" />
                                </Flex>
                              )}
                              <Text fontSize="sm" fontWeight="bold" color={textColor} maxW="200px" isTruncated title={product.name}>
                                {product.name}
                              </Text>
                            </HStack>
                          </Td>

                          {/* 2. کد حسابداری */}
                          <Td borderColor={borderColor}>
                            <Code fontSize="xs" bg={skuCodeBg} p={1} rounded="md">
                              {product.accountingCode || '—'}
                            </Code>
                          </Td>

                          {/* 3. وضعیت موجودی */}
                          <Td borderColor={borderColor}>
                            {(() => {
                              if (lv.length > 0) {
                                return (
                                  <VStack align="start" spacing={0}>
                                    <Text fontSize="md" fontWeight="black" color={totalLogicalStock > 10 ? 'green.500' : totalLogicalStock > 0 ? 'orange.500' : 'red.500'}>
                                      {totalLogicalStock} <Text as="span" fontSize="xs" fontWeight="normal" color={mutedColor}>عدد</Text>
                                    </Text>
                                    <Text fontSize="xs" color="gray.500">{lv.length} تنوع</Text>
                                  </VStack>
                                );
                              }
                              if (totalVariants > 0) {
                                return (
                                  <VStack align="start" spacing={0}>
                                    <Text fontSize="md" fontWeight="black" color={totalVariantStock > 10 ? 'green.500' : totalVariantStock > 0 ? 'orange.500' : 'red.500'}>
                                      {totalVariantStock} <Text as="span" fontSize="xs" fontWeight="normal" color={mutedColor}>عدد</Text>
                                    </Text>
                                    <Text fontSize="xs" color="gray.500">{totalVariants} تنوع درگاه</Text>
                                  </VStack>
                                );
                              }
                              return (
                                <Text fontSize="md" fontWeight="black" color={stock > 10 ? 'green.500' : stock > 0 ? 'orange.500' : 'red.500'}>
                                  {stock} <Text as="span" fontSize="xs" fontWeight="normal" color={mutedColor}>عدد</Text>
                                </Text>
                              );
                            })()}
                          </Td>

                          {/* 4. درگاه‌های متصل */}
                          <Td borderColor={borderColor} maxW="200px">
                            {refs.length > 0 ? (
                              <Flex flexWrap="wrap" gap={1}>
                                {refs.map((ref) => {
                                  const gwName = ref.gateway?.name || (typeof ref.gateway === 'string' ? ref.gateway : '—');
                                  const type = ref.gateway?.type;
                                  return (
                                    <Badge
                                      key={ref._id || ref.gateway?._id || ref.gateway}
                                      bg={type === 'basalam' ? 'orange.500' : type === 'digikala' ? 'red.500' : 'purple.500'}
                                      color="white"
                                      fontSize="10px"
                                      px={2} py={0.5}
                                      borderRadius="md"
                                      shadow="sm"
                                    >
                                      {gwName}
                                    </Badge>
                                  );
                                })}
                              </Flex>
                            ) : (
                              <Text fontSize="xs" color="gray.400">بدون اتصال</Text>
                            )}
                          </Td>

                          {/* 5. وضعیت */}
                          <Td borderColor={borderColor}>
                            <Switch
                              isChecked={product.isActive !== false}
                              colorScheme="green"
                              size="md"
                              onChange={async (e) => {
                                try {
                                  await updateProduct(product._id, { isActive: e.target.checked });
                                  queryClient.invalidateQueries(['products']);
                                  toast.success('وضعیت محصول با موفقیت تغییر کرد');
                                } catch (err) {
                                  toast.error(err?.response?.data?.message || 'خطا در تغییر وضعیت');
                                }
                              }}
                            />
                          </Td>

                          {/* 6. عملیات */}
                          <Td borderColor={borderColor} textAlign="center">
                            <Flex justify="center" gap={2} wrap="wrap">
                              <Tooltip label="تغییر موجودی" hasArrow placement="top">
                                <Button size="sm" colorScheme="blue" variant="light" bg="blue.50" color="blue.600" _hover={{ bg: "blue.100" }} onClick={() => openStockModal(product)}>
                                  <FaBoxOpen />
                                </Button>
                              </Tooltip>
                              
                              <Tooltip label="فورس سینک" hasArrow placement="top">
                                <Button size="sm" colorScheme="teal" variant="light" bg="teal.50" color="teal.600" _hover={{ bg: "teal.100" }} onClick={() => handleSyncStock(product)} isLoading={syncStockMutation.isLoading} isDisabled={!refs.length}>
                                  <FaSync />
                                </Button>
                              </Tooltip>

                              <HStack spacing={1} bg={actionsBarBg} p={1} rounded="lg">
                                <Tooltip label="صفحه محصول" hasArrow placement="top">
                                  <IconButton
                                    size="xs"
                                    variant="ghost"
                                    colorScheme="purple"
                                    icon={<FaExternalLinkAlt />}
                                    onClick={() => window.open(getProductPublicPageUrl(product._id), '_blank', 'noopener,noreferrer')}
                                  />
                                </Tooltip>
                                <Tooltip label="نقشه اتصال تنوع‌ها" hasArrow placement="top">
                                  <IconButton size="xs" variant="ghost" colorScheme="gray" icon={<FaInfoCircle />} onClick={() => setDetailsModalProduct(product)} />
                                </Tooltip>
                                <Tooltip label="ویرایش" hasArrow placement="top">
                                  <IconButton size="xs" variant="ghost" colorScheme="blue" icon={<FaEdit />} onClick={() => navigate(`/products/${product._id}/edit`)} />
                                </Tooltip>
                                <Tooltip label="حذف" hasArrow placement="top" bg="red.500">
                                  <IconButton size="xs" variant="ghost" colorScheme="red" icon={<FaTrash />} onClick={() => handleDeleteProduct(product._id)} />
                                </Tooltip>
                              </HStack>
                            </Flex>
                          </Td>
                        </Tr>
                      );
                    })
                  )}
                </Tbody>
              </Table>
            </Box>

            {/* Pagination */}
            {pagination.pages > 1 && (
              <Flex justify="center" align="center" p={4} borderTopWidth="1px" borderColor={borderColor} gap={2} bg={paginationBg}>
                <Button size="sm" borderRadius="lg" onClick={() => setCurrentPage(1)} isDisabled={currentPage === 1}>اولین</Button>
                <Button size="sm" borderRadius="lg" onClick={() => setCurrentPage(p => Math.max(1, p - 1))} isDisabled={currentPage === 1}>قبلی</Button>
                <Badge px={3} py={1} borderRadius="lg" colorScheme="blue" variant="subtle">صفحه {pagination.page} از {pagination.pages}</Badge>
                <Button size="sm" borderRadius="lg" onClick={() => setCurrentPage(p => Math.min(pagination.pages, p + 1))} isDisabled={currentPage === pagination.pages}>بعدی</Button>
                <Button size="sm" borderRadius="lg" onClick={() => setCurrentPage(pagination.pages)} isDisabled={currentPage === pagination.pages}>آخرین</Button>
              </Flex>
            )}
          </Box>
        </Box>
      </Flex>

      <style>{`
        .custom-scrollbar::-webkit-scrollbar { height: 6px; }
        .custom-scrollbar::-webkit-scrollbar-track { background: transparent; }
        .custom-scrollbar::-webkit-scrollbar-thumb { background: #cbd5e1; border-radius: 10px; }
      `}</style>

      {/* ========================================== */}
      {/* Modal تنظیم دستی موجودی */}
      {/* ========================================== */}
      <CustomModal
        isOpen={!!stockModalProduct}
        onClose={handleCloseStockModal}
        title="تغییر موجودی محصول"
        onSubmit={handleSubmitStock}
        submitText="اعمال موجودی"
        isLoading={updateStockMutation.isLoading}
      >
        {stockModalProduct && (
          <VStack align="stretch" spacing={6}>
            <Flex align="center" gap={4} p={4} bg={stockModalHeaderBg} borderRadius="xl">
              {stockModalProduct.image ? (
                <Image src={stockModalProduct.image} boxSize="60px" objectFit="cover" borderRadius="lg" shadow="sm" />
              ) : (
                <Flex boxSize="60px" bg="white" borderRadius="lg" align="center" justify="center" shadow="sm">
                  <Icon as={FaBox} color="gray.300" />
                </Flex>
              )}
              <Box>
                <Text fontWeight="bold" fontSize="md" color={textColor}>{stockModalProduct.name}</Text>
                <Text fontSize="xs" color={stockModalHeaderSubTextColor} mt={1}>
                  موجودی جدید در دیتابیس و تمام درگاه‌های متصل سینک می‌شود.
                </Text>
              </Box>
            </Flex>

            <Box>
              <FormLabel fontWeight="bold" fontSize="sm" color={mutedColor}>سطح اعمال تغییرات</FormLabel>
              <Flex gap={3} flexDir={{ base: "column", md: "row" }}>
                <Box
                  flex={1} p={4} borderWidth="2px" borderRadius="xl" cursor="pointer" transition="all 0.2s"
                  borderColor={stockApplyTo === 'product' ? 'blue.500' : borderColor}
                  bg={stockApplyTo === 'product' ? stockLevelHighlightBg : 'transparent'}
                  onClick={() => setStockApplyTo('product')}
                >
                  <Text fontWeight="bold" color={textColor} mb={1}>کل محصول</Text>
                  <Text fontSize="xs" color={mutedColor}>موجودی روی تمام تنوع‌ها یکسان تنظیم می‌شود.</Text>
                </Box>
                
                {stockModalProduct.logicalVariants?.length > 0 && (
                  <Box
                    flex={1} p={4} borderWidth="2px" borderRadius="xl" cursor="pointer" transition="all 0.2s"
                    borderColor={stockApplyTo === 'variant' ? 'blue.500' : borderColor}
                    bg={stockApplyTo === 'variant' ? stockLevelHighlightBg : 'transparent'}
                    onClick={() => setStockApplyTo('variant')}
                  >
                    <Text fontWeight="bold" color={textColor} mb={1}>یک تنوع خاص</Text>
                    <Text fontSize="xs" color={mutedColor}>فقط موجودی یک سایز/رنگ آپدیت می‌شود.</Text>
                  </Box>
                )}
              </Flex>
            </Box>

            {stockApplyTo === 'variant' && stockModalProduct.logicalVariants?.length > 0 && (
              <>
                <FormControl>
                  <FormLabel fontWeight="bold" fontSize="sm">انتخاب تنوع</FormLabel>
                  <Select
                    value={stockLogicalVariantId}
                    onChange={handleVariantSelectChange}
                    size="lg" borderRadius="xl"
                    bg={stockVariantSelectBg}
                  >
                    {stockModalProduct.logicalVariants.map((lv) => (
                      <option key={lv._id} value={lv._id}>
                        {lv.accountingCode || '(بدون کد)'} — موجودی: {lv.stock ?? 0} | قیمت پایه: {lv.price ? `${lv.price.toLocaleString('fa-IR')} ت` : 'بدون قیمت'}
                      </option>
                    ))}
                  </Select>
                </FormControl>

                <FormControl>
                  <FormLabel fontWeight="bold" fontSize="sm">قیمت پایه تنوع (تومان)</FormLabel>
                  <NumberInput min={0} value={stockPriceValue} onChange={(_, val) => setStockPriceValue(isNaN(val) ? 0 : val)} size="lg">
                    <NumberInputField borderRadius="xl" bg={stockNumberInputBg} fontWeight="bold" fontSize="lg" dir="ltr" />
                  </NumberInput>
                  <Text fontSize="xs" color={mutedColor} mt={1}>
                    قیمت این تنوع برای هر درگاه متصل بر اساس درصد مشخص‌شده محاسبه و اصلاح می‌شود.
                  </Text>
                </FormControl>
              </>
            )}

            <FormControl>
              <FormLabel fontWeight="bold" fontSize="sm">عدد موجودی جدید</FormLabel>
              <NumberInput min={0} value={stockValue} onChange={(_, val) => setStockValue(isNaN(val) ? 0 : val)} size="lg">
                <NumberInputField borderRadius="xl" bg={stockNumberInputBg} fontWeight="bold" fontSize="xl" />
              </NumberInput>
            </FormControl>

            {stockModalProduct.gatewayRefs?.length > 0 && (
              <Box pt={4} borderTopWidth="1px" borderColor={borderColor}>
                <FormLabel fontWeight="bold" fontSize="sm" color={mutedColor} mb={3}>گزارش زنده سینک با درگاه‌ها</FormLabel>
                <VStack align="stretch" spacing={2}>
                  {stockModalProduct.gatewayRefs.map((ref) => {
                    const gwName = ref.gateway?.name || (typeof ref.gateway === 'string' ? ref.gateway : '—');
                    const status = stockGatewayStatuses[gwName] || 'idle';
                    return (
                      <Flex key={ref._id || gwName} align="center" justify="space-between" p={3} bg={stockGatewayRowBg} borderRadius="lg">
                        <Text fontSize="sm" fontWeight="bold" color={textColor}>{gwName}</Text>
                        <HStack>
                          {status === 'loading' && <Spinner size="sm" color="blue.500" />}
                          {status === 'success' && <Badge colorScheme="green" borderRadius="md"><CheckIcon mr={1}/>موفق</Badge>}
                          {status === 'error' && <Badge colorScheme="red" borderRadius="md"><WarningIcon mr={1}/>خطا</Badge>}
                          {status === 'idle' && <Text fontSize="xs" color={mutedColor}>آماده ارسال</Text>}
                        </HStack>
                      </Flex>
                    );
                  })}
                </VStack>
              </Box>
            )}
          </VStack>
        )}
      </CustomModal>

      {/* ========================================== */}
      {/* Modal جزئیات موجودی و تنوع‌ها */}
      {/* ========================================== */}
      <CustomModal
        isOpen={!!detailsModalProduct}
        onClose={() => setDetailsModalProduct(null)}
        title="نقشه اتصال تنوع‌ها"
        size="2xl"
      >
        {detailsModalProduct && (
          <ProductVariantTree product={detailsModalProduct} />
        )}
      </CustomModal>
    </>
  );
}